import { feasibleCount, limitingAxes, type CapacityEnvelope, type RequestedCapacity } from '../../core/capacityEnvelope'

const axisLabels = {
  cpu: 'CPU',
  memory: 'memory',
  storage: 'storage',
  gpu: 'GPU',
} as const

export function SizeEnvelope({ envelope, requested, replicas, isLoading = false, error }: { envelope?: CapacityEnvelope | null; requested: RequestedCapacity; replicas: number; isLoading?: boolean; error?: unknown }) {
  if (isLoading) {
    return <div className="notice notice-info" role="status"><strong>Checking network capacity…</strong><p>The sliders stay usable while the latest observation loads.</p></div>
  }

  if (error || !envelope || !envelope.dataAvailable) {
    return <div className="notice notice-caution" role="status"><strong>Network capacity could not be checked.</strong><p>We left the sliders unbounded. You can continue, but placement may take longer while providers respond.</p></div>
  }

  // At provider resolution this counts providers, not rows: one provider can
  // contribute several rows (C164-3).
  const count = feasibleCount(envelope, requested, replicas)
  const axes = limitingAxes(envelope.hosts, requested, replicas)
  const providerResolution = envelope.resolution === 'provider'
  const noun = providerResolution ? 'provider' : 'host'
  const plural = count === 1 ? noun : `${noun}s`
  const capacityClaim = providerResolution
    ? `At most ${count} ${plural} have enough reported capacity for this configuration.`
    : `${count} ${plural} can run this configuration.`
  const axisText = axes.map((axis) => axisLabels[axis]).join(', ')

  return <div className={`notice ${count === 0 ? 'notice-caution' : 'notice-info'}`} role={count === 0 ? 'alert' : 'status'}>
    <strong>{capacityClaim}</strong>
    {providerResolution && <p>Provider-level totals are an upper bound; an individual host still needs to confirm placement.</p>}
    {envelope.staleHosts > 0 && <p>{envelope.staleHosts} {noun} observation{envelope.staleHosts === 1 ? '' : 's'} {envelope.staleHosts === 1 ? 'is' : 'are'} stale and excluded.</p>}
    {count === 0 && <p>{axisText ? `${axisText} ${axes.length === 1 ? 'excludes' : 'exclude'} every observed ${noun}. Reduce the requested size and try again.` : `No single ${noun} has all requested resources together. Reduce the requested size and try again.`}</p>}
  </div>
}
